import React, { useEffect, useState } from 'react'
import { Form, Input, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js'
import { CheckCircleTwoTone, QuestionCircleOutlined, QuestionCircleTwoTone } from '@ant-design/icons'
import * as PropTypes from 'prop-types'
import { mainTwoToneColor } from '../../../variables'

const PhoneField = ({
  initialValue = '',
  required = false,
  validateStatus = 'warning',
  fieldName = 'phoneNumber',
  numberValid = false,
  label,
  getFieldValue,
  suffixName = 'suffix',
  showValidation = false,
  disabled = false,
  formName,
  onBlur,
  setFieldsValue,
  hasCountry
}) => {
  const { t } = useTranslation()

  const phoneNumber = isValidPhoneNumber(initialValue?.replace('_', '') || '')

  const [isWrongNumber, setIsWrongNumber] = useState(initialValue && (!phoneNumber || !numberValid))

  useEffect(() => {
    setIsWrongNumber(initialValue && (!phoneNumber || !numberValid))
  }, [initialValue, numberValid, phoneNumber])

  const handlePaste = e => {
    const pastedData = e.clipboardData.getData('Text')
    if (pastedData && setFieldsValue && getFieldValue) {
      let text = pastedData

      if (text.startsWith('00')) {
        text = text.replace('00', '+')
      }

      let parsedNumber = parsePhoneNumberFromString(text)

      if (!parsedNumber) {
        parsedNumber = parsePhoneNumberFromString(`${getFieldValue(suffixName)}${text}`)
      }

      if (!parsedNumber) {
        parsedNumber = parsePhoneNumberFromString(`+${text}`)
      }

      if (parsedNumber) {
        // Set the country code to the suffixName field
        setFieldsValue({
          [suffixName]: `+${parsedNumber.countryCallingCode}`,
          [fieldName]: parsedNumber.nationalNumber
        })
        if (hasCountry) {
          setFieldsValue({
            country: parsedNumber.country
          })
        }
        setIsWrongNumber(false)
      } else {
        setFieldsValue({
          [fieldName]: pastedData
        })
        setIsWrongNumber(true)
      }

      e.preventDefault() // Prevent the default paste behavior
    }
  }

  return (
    <Form.Item
      className="phoneNumberField"
      name={fieldName}
      hasFeedback
      validateStatus={showValidation ? (isWrongNumber ? validateStatus : phoneNumber ? 'success' : '') : ''}
      label={
        label || (
          <Tooltip
            className="tooltip-icon"
            title={isWrongNumber ? t('common.phoneInvalid') : numberValid ? t('common.phoneValid') : t('common.phonePopper')}
          >
            {t('common.yourPhone')}
            &nbsp;
            {isWrongNumber ? (
              <QuestionCircleTwoTone className="scaleIfRTL" twoToneColor="#faad14" />
            ) : numberValid ? (
              <CheckCircleTwoTone twoToneColor={mainTwoToneColor} />
            ) : (
              <QuestionCircleOutlined />
            )}
          </Tooltip>
        )
      }
      rules={[
        { required, message: t('common.phoneRequired') },
        { max: 50, message: t('common.phoneTooLong') },
        {
          validator: async (rule, value) => setIsWrongNumber(!isValidPhoneNumber(`${getFieldValue(suffixName)}${value}`))
        }
      ]}
    >
      <Input
        autoComplete="tel-national"
        type="tel"
        disabled={disabled}
        dir="ltr"
        onPaste={handlePaste}
        onBlur={onBlur}
        data-testid={`${formName || 'phoneField'}-${fieldName}`}
        addonBefore={
          <Form.Item name={suffixName} noStyle>
            <Input
              disabled={disabled}
              controls={false}
              type="tel"
              dir="ltr"
              autoComplete="tel-country-code"
              data-testid={`${formName || 'phoneField'}-${fieldName}-${suffixName || 'code'}`}
            />
          </Form.Item>
        }
      />
    </Form.Item>
  )
}

PhoneField.propTypes = {
  initialValue: PropTypes.string,
  required: PropTypes.bool,
  hasCountry: PropTypes.bool,
  validateStatus: PropTypes.string,
  fieldName: PropTypes.string,
  numberValid: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  showValidation: PropTypes.bool,
  label: PropTypes.string,
  getFieldValue: PropTypes.func.isRequired,
  setFieldsValue: PropTypes.func.isRequired,
  onBlur: PropTypes.func
}

export default PhoneField
